import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ModalDirective, ModalModule } from 'ngx-bootstrap/modal';
import { AnnualDetailsNavigationEventService } from '../annual-details-event.service';
import { NavigationState } from '../annual-details-navigation.service';
import { AnnualDetailsService } from '../annual-details.service';
import { AddYearTaxableValueSelection, AnnualDetailYear, AnnualDetailYearList } from '../Annual-Year/annual-year.model';
import { FeatureFlagsService } from '../../Common/FeatureFlags/feature-flags-service';
import { MessageModalService } from '../../UI-Lib/Message-Box/messageModal.service';
import { HelpModule } from '../../UI-Lib/Help-Tooltip/help.module';
import { FormsModule } from '@angular/forms';
import { ModalWrapperComponent } from '../../UI-Lib/Modal-Wrapper/modalWrapper.component';

declare const _: any;

import CalcTvUsingTypeEnum = Weissman.Model.CalcTvUsingTypeEnum;

@Component({
    selector: 'add-year-modal',
    templateUrl: './add-annual-year.component.html',
    imports: [FormsModule, HelpModule, ModalModule, ModalWrapperComponent],
})
export class AddYearModalComponent implements OnInit, OnChanges {
    constructor(
        private annualDetailsService: AnnualDetailsService,
        private navigationEvent: AnnualDetailsNavigationEventService,
        private featureFlagsService: FeatureFlagsService,
        private messageModalService: MessageModalService,
    ) {}

    @Input() parcelId: number;
    @Input() yearsList: AnnualDetailYearList;
    @Input() navigationState: NavigationState;
    @Input() showButton: boolean = true;
    @ViewChild('addYearModal', { static: true }) addYearModal: ModalDirective;

    //currentYears: any;
    selectedYear: number;
    years: AnnualDetailYear[];
    saveEnabled: boolean = true;
    loading: boolean = false;
    showTaxableValueControls: boolean = false;
    // True while the dialog was opened by a save or task completion that did not create the year, where declining
    // means the year is not created rather than undoing that operation.
    isPrompted: boolean = false;
    autoCalculateTaxableValue: boolean = true;
    calcTVUsing: CalcTvUsingTypeEnum = CalcTvUsingTypeEnum.SystemLogic;
    systemLogic: CalcTvUsingTypeEnum = CalcTvUsingTypeEnum.SystemLogic;
    priorYearSetup: CalcTvUsingTypeEnum = CalcTvUsingTypeEnum.PriorYearSetup;

    private sourceYear: AnnualDetailYear = null;
    private requestedYear: number = null;
    private closedResolver: () => void = null;

    ngOnInit(): void {
        this.initializeYearData();
    }

    // Reloading the years replaces the list object, which has no options or taxable value source yet, and that can
    // happen after the dialog was already shown from the previous list.
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.yearsList && !changes.yearsList.firstChange) {
            this.initializeYearData();
        }
    }

    showModalInit(): void {
        this.initializeYearData();
    }

    // Opens the dialog with the given year selected; resolves when the dialog closes, whether the year was added,
    // the user cancelled, or the dialog was dismissed.
    open(year: number): Promise<void> {
        this.requestedYear = year;
        this.isPrompted = true;

        return new Promise<void>((resolve) => {
            this.closedResolver = resolve;
            this.addYearModal.show();
        });
    }

    modalHidden(): void {
        this.requestedYear = null;
        this.isPrompted = false;

        const resolve = this.closedResolver;
        this.closedResolver = null;
        resolve?.();
    }

    initializeYearData(): void {
        this.years = this.yearsList.years;
        let currentYears = _.map(this.years, 'annualYear1');
        let parcelHasYears = true;

        if (currentYears.length === 0) {
            const d = new Date();
            currentYears = [d.getFullYear()];

            parcelHasYears = false;
        }

        const latestYear = _.maxBy(currentYears);
        const earliestYear = _.minBy(currentYears);
        const yearsArray = _.range(earliestYear - 5, latestYear + 5);

        // this.yearsList.options = _.filter(yearsArray, (element) => {
        //     return currentYears.indexOf(element) < 0; // || !this.parcelHasYears;
        // });

        yearsArray.sort();
        yearsArray.reverse();

        this.yearsList.options = _.map(yearsArray, function (year) {
            return {
                year: year,
                disabled: _.includes(currentYears, year) && parcelHasYears,
            };
        });

        this.selectedYear = this.requestedYear ?? (parcelHasYears ? latestYear + 1 : new Date().getFullYear());
        this.updateTaxableValueSource();
    }

    selectedYearChanged(year: number): void {
        this.selectedYear = year;
        this.updateTaxableValueSource();
    }

    autoCalculateTaxableValueChanged(): void {
        // Calc TV Using only applies while Auto Calculate TV is checked, so checking it again starts from the source value.
        if (this.autoCalculateTaxableValue && this.sourceYear) {
            this.calcTVUsing = this.sourceYear.calcTVUsing;
        }
    }

    async save(): Promise<void> {
        if (!this.addYearModal?.isShown || this.loading || !this.saveEnabled || !this.parcelId || !this.selectedYear) {
            return;
        }

        this.loading = true;
        this.saveEnabled = false;

        try {
            if (!(await this.addYear())) {
                return;
            }

            //this instead of refreshGrid because the add years is acceesible ON the grid
            this.navigationState.resetYearsHandler();
            this.addYearModal.hide();
            // Update parcel activity panel
            this.navigationEvent.refreshActivityData();
        } finally {
            this.loading = false;
            this.saveEnabled = true;
        }
    }

    cancel(): void {
        return null;
    }

    // Adds the year; returns false when the user declined the confirmation, in which case nothing was created.
    private async addYear(): Promise<boolean> {
        if (!this.showTaxableValueControls) {
            await this.annualDetailsService.addYear(this.parcelId, this.selectedYear, true, true);
            return true;
        }

        const selection: AddYearTaxableValueSelection = {
            autoCalculateTaxableValue: this.autoCalculateTaxableValue,
            calcTVUsing: this.calcTVUsing,
        };

        try {
            await this.annualDetailsService.addYear(this.parcelId, this.selectedYear, true, true, selection);
        } catch (e) {
            // The service returns a 422 and a message when the user has to confirm that nothing is carried forward.
            if (e?.status !== 422) {
                throw e;
            }

            try {
                await this.messageModalService.confirm(e.error.message, 'Confirm Add Year');
            } catch {
                return false;
            }

            await this.annualDetailsService.addYear(this.parcelId, this.selectedYear, true, true, selection, true);
        }

        return true;
    }

    // The source is the most recent loaded year before the selected one. The grid only loads the latest years, and the
    // years it leaves out are older than every loaded year, so this is the real source whenever one is found.
    private updateTaxableValueSource(): void {
        this.sourceYear = null;

        if (this.featureFlagsService.featureFlags?.enableTaxableValueOverride) {
            const earlierYears = (this.years ?? []).filter((year) => year.annualYear1 < this.selectedYear);
            this.sourceYear = _.maxBy(earlierYears, 'annualYear1') ?? null;
        }

        this.showTaxableValueControls =
            !!this.sourceYear &&
            (!this.sourceYear.autoCalculateTaxableValue ||
                this.sourceYear.calcTVUsing === CalcTvUsingTypeEnum.PriorYearSetup);

        if (this.showTaxableValueControls) {
            this.autoCalculateTaxableValue = this.sourceYear.autoCalculateTaxableValue;
            this.calcTVUsing = this.sourceYear.calcTVUsing;
        }
    }
}
